import { generateGuid } from "@entrilia/shared/utilities/generateGuid.ts";
import type { ConditionDescriptor, ConditionOperator } from "../../../../../api/types/fieldConfigurationTypes.ts";
import type { ConditionField, DimensionDescriptor } from "../../../../../api/types/svTypes.ts";
import { AreaItemType } from "../../../../../api/types/svTypes.ts";
import {
  createLeafConditionDescriptor,
  createNarrowingConditionDescriptor,
} from "../utilities/conditionDescriptorHelper.ts";

export type DrillDownConditionLeafNode = {
  id: string;
  type: "leaf";
  field: ConditionField;
};

export type DrillDownConditionGroupNode = {
  id: string;
  type: "group";
  operator: ConditionOperator;
  conditions: DrillDownConditionNode[];
};

export type DrillDownConditionNode = DrillDownConditionLeafNode | DrillDownConditionGroupNode;

export function mapConditionDescriptorsToTree(
  conditions: ConditionDescriptor[],
  dimensions: DimensionDescriptor[]
): DrillDownConditionNode[] {
  return conditions
    .map((condition) => mapConditionDescriptorToNode(condition, dimensions))
    .filter((condition): condition is DrillDownConditionNode => condition !== undefined);
}

function mapConditionDescriptorToNode(
  condition: ConditionDescriptor,
  dimensions: DimensionDescriptor[]
): DrillDownConditionNode | undefined {
  if (condition.filter !== undefined) {
    const meta = dimensions.find((dimension) => dimension.name === condition.filter?.dimensionName);
    if (meta === undefined) {
      return undefined;
    }

    return {
      id: generateGuid(),
      type: "leaf",
      field: {
        meta,
        areaItemType: AreaItemType.CONDITIONS,
        config: {
          guid: generateGuid(),
          filter: structuredClone(condition.filter),
        },
      },
    };
  }

  const nestedConditions = condition.conditions
    .map((item) => mapConditionDescriptorToNode(item, dimensions))
    .filter((item): item is DrillDownConditionNode => item !== undefined);
  if (nestedConditions.length === 0) {
    return undefined;
  }

  return {
    id: generateGuid(),
    type: "group",
    operator: condition.operator ?? "And",
    conditions: nestedConditions,
  };
}

export function mapTreeToConditionDescriptors(
  conditions: DrillDownConditionNode[],
  withLinked = false
): ConditionDescriptor[] {
  return conditions
    .map((condition) => mapTreeNodeToDescriptor(condition, withLinked))
    .filter((condition): condition is ConditionDescriptor => condition !== undefined);
}

function mapTreeNodeToDescriptor(
  condition: DrillDownConditionNode,
  withLinked: boolean
): ConditionDescriptor | undefined {
  if (condition.type === "leaf") {
    const { field } = condition;
    if (field.validation?.invalid) {
      return undefined;
    }
    if (!withLinked && field.hasLinks) {
      return undefined;
    }
    if (!field.config?.filter?.values || field.config.filter.values.length === 0) {
      return undefined;
    }

    return createLeafConditionDescriptor(structuredClone(field.config.filter));
  }

  const nestedConditions = condition.conditions
    .map((item) => mapTreeNodeToDescriptor(item, withLinked))
    .filter((item): item is ConditionDescriptor => item !== undefined);
  if (nestedConditions.length === 0) {
    return undefined;
  }

  return {
    operator: condition.operator,
    conditions: nestedConditions,
  };
}

export function getDrillDownConditionLeafFields(conditions: DrillDownConditionNode[]): ConditionField[] {
  return conditions.flatMap((condition) => getConditionLeafFields(condition));
}

export const getDrillDownNarrowingFilters = (
  conditions: DrillDownConditionNode[],
  field: ConditionField
): ConditionDescriptor[] => {
  let currentFieldReached = false;
  const collectFilters = (nodes: DrillDownConditionNode[]): ConditionDescriptor[] => {
    const filters: ConditionDescriptor[] = [];
    for (const node of nodes) {
      if (currentFieldReached) {
        break;
      }
      if (node.type === "leaf") {
        currentFieldReached = node.field.config.guid === field.config.guid;
        const filter = currentFieldReached ? undefined : createNarrowingConditionDescriptor(node.field);
        if (filter !== undefined) {
          filters.push(filter);
        }
        continue;
      }

      const nestedFilters = collectFilters(node.conditions);
      if (nestedFilters.length > 0) {
        filters.push({ operator: node.operator, conditions: nestedFilters });
      }
    }
    return filters;
  };

  return collectFilters(conditions);
};

function getConditionLeafFields(condition: DrillDownConditionNode): ConditionField[] {
  if (condition.type === "leaf") {
    return [condition.field];
  }

  return condition.conditions.flatMap(getConditionLeafFields);
}

export function addTopLevelDrillDownCondition(
  conditions: DrillDownConditionNode[],
  field: ConditionField
): DrillDownConditionNode[] {
  return [
    ...conditions,
    {
      id: generateGuid(),
      type: "leaf",
      field,
    },
  ];
}

export function updateDrillDownConditionField(
  conditions: DrillDownConditionNode[],
  field: ConditionField,
  changes: Partial<ConditionField>
): DrillDownConditionNode[] {
  return conditions.map((condition) => updateTreeCondition(condition, field, changes));
}

function updateTreeCondition(
  condition: DrillDownConditionNode,
  field: ConditionField,
  changes: Partial<ConditionField>
): DrillDownConditionNode {
  if (condition.type === "leaf") {
    if (condition.field.config.guid !== field.config.guid) {
      return condition;
    }

    return {
      ...condition,
      field: mergeConditionField(condition.field, changes),
    };
  }

  return {
    ...condition,
    conditions: condition.conditions.map((item) => updateTreeCondition(item, field, changes)),
  };
}

function mergeConditionField(field: ConditionField, changes: Partial<ConditionField>): ConditionField {
  const nextConfig = changes.config ? { ...field.config, ...changes.config } : field.config;
  return {
    ...field,
    ...changes,
    config: nextConfig,
  };
}

export function removeDrillDownConditionField(
  conditions: DrillDownConditionNode[],
  field: ConditionField
): DrillDownConditionNode[] {
  return conditions
    .map((condition) => removeTreeCondition(condition, field))
    .filter((condition): condition is DrillDownConditionNode => condition !== undefined);
}

function removeTreeCondition(
  condition: DrillDownConditionNode,
  field: ConditionField
): DrillDownConditionNode | undefined {
  if (condition.type === "leaf") {
    return condition.field.config.guid === field.config.guid ? undefined : condition;
  }

  const nestedConditions = condition.conditions
    .map((item) => removeTreeCondition(item, field))
    .filter((item): item is DrillDownConditionNode => item !== undefined);
  if (nestedConditions.length === 0) {
    return undefined;
  }

  return {
    ...condition,
    conditions: nestedConditions,
  };
}
